import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';
import MfaUserForm from './MfaUserForm'; // Import the new form component
import ApiTokenManager from './ApiTokenManager'; // Import the new component
import PaginationControls from '../../components/PaginationControls';
import SidebarPanel from '../../components/admin/SidebarPanel';
import AgGridTable from '../../components/AgGridTable';

const MfaModulePage = () => {
  const [mfaUsers, setMfaUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false); 
  const [editingUser, setEditingUser] = useState(null); 
  const [showOtpDisplay, setShowOtpDisplay] = useState(false);
  const [otpUser, setOtpUser] = useState(null);
  const [showApiTokenSidebar, setShowApiTokenSidebar] = useState(false);
  const [selectedUserForApiToken, setSelectedUserForApiToken] = useState(null);
  const [showAuditTrailSidebar, setShowAuditTrailSidebar] = useState(false);
  const [selectedUserForAuditTrail, setSelectedUserForAuditTrail] = useState(null);
  const [mfaUserLogs, setMfaUserLogs] = useState([]);
  const [mfaUserLogsLoading, setMfaUserLogsLoading] = useState(false);
  const [mfaUserLogsError, setMfaUserLogsError] = useState(null);
  const [showLogsSection, setShowLogsSection] = useState(true); // State to control visibility of logs section
  const [mfaLogsPagination, setMfaLogsPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    page_size: 50,
    has_next: false,
    has_previous: false
  });
  const [mfaLogsCurrentPage, setMfaLogsCurrentPage] = useState(1);

  const fetchMfaUsers = async () => {
    try {
      setLoading(true);
      const response = await adminApi.get('/admin/mfa/users/');
      if (response.data && response.data.success) {
        setMfaUsers(response.data.mfa_users); // Assuming the backend returns 'mfa_users'
      } else {
        setError(response.data?.message || 'Failed to fetch MFA users');
      }
    } catch (err) {
      console.error('Error fetching MFA users:', err);
      setError(err.message || 'An error occurred while fetching MFA users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMfaUsers();
  }, []);

  // Effect to add/remove 'open' class for sidebar animation
  useEffect(() => {
    const sidebar = document.querySelector('.sidebar-panel');
    if (sidebar) {
      if (showForm || showOtpDisplay || showApiTokenSidebar || showAuditTrailSidebar) { 
        sidebar.classList.add('open');
      } else {
        sidebar.classList.remove('open');
      }
    }
  }, [showForm, showOtpDisplay, showApiTokenSidebar, showAuditTrailSidebar]);

  const handleCreateClick = () => {
    setEditingUser(null); // Clear any editing data
    setShowForm(true);
  };

  const handleEditClick = (user) => {
    setEditingUser(user);
    setShowForm(true);
    setShowOtpDisplay(false);
    setOtpUser(null);
  };

  const handleGenerateTotpClick = (user) => {
    setOtpUser(user);
    setShowOtpDisplay(true);
    setShowForm(false);
    setEditingUser(null);
    setMfaLogsCurrentPage(1);
    fetchMfaUserLogs(user.email, false, 1);
  };

  const handleAuditTrailClick = (user) => {
    setSelectedUserForAuditTrail(user);
    setShowAuditTrailSidebar(true);
    setShowForm(false);
    setShowOtpDisplay(false);
    setShowApiTokenSidebar(false);
    setEditingUser(null);
    setMfaLogsCurrentPage(1);
    fetchMfaUserLogs(user.email, true, 1);
  };

  const handleGenerateApiTokenClick = (user) => {
    setSelectedUserForApiToken(user);
    setShowApiTokenSidebar(true);
    setShowForm(false);
    setShowOtpDisplay(false);
  };

  const handleSave = () => {
    setShowForm(false); // Close the form
    setShowOtpDisplay(false); // Close OTP display
    setShowApiTokenSidebar(false);
    fetchMfaUsers(); // Refresh the list of MFA users
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingUser(null); // Clear editing data when form is closed
    setShowOtpDisplay(false); // Close OTP display
    setOtpUser(null); // Clear OTP user
    setMfaUserLogs([]); // Clear logs when closing
    setMfaUserLogsError(null); // Clear log errors
    setShowApiTokenSidebar(false);
    setSelectedUserForApiToken(null);
    setShowAuditTrailSidebar(false);
    setSelectedUserForAuditTrail(null);
    setMfaLogsCurrentPage(1);
    setMfaLogsPagination({
      current_page: 1,
      total_pages: 1,
      total_count: 0,
      page_size: 50,
      has_next: false,
      has_previous: false
    });
  };
  
  const handleMfaLogsPageChange = async (newPage) => {
    if (selectedUserForAuditTrail && newPage !== mfaLogsCurrentPage) {
      setMfaLogsCurrentPage(newPage);
      await fetchMfaUserLogs(selectedUserForAuditTrail.email, true, newPage);
    }
  };

  const fetchMfaUserLogs = async (email, showAllLogs = false, page = 1) => {
    setMfaUserLogsLoading(true);
    setMfaUserLogsError(null);
    try {
      const user = mfaUsers.find(u => u.email === email);
      if (!user) {
        throw new Error('User not found');
      }

      const response = await adminApi.get(`/admin/mfa/users/${user.id}/logs/`, {
        params: {
          filter: showAllLogs ? 'all' : 'totp',
          page: page
        }
      });
      
      if (response.data && response.data.success) {
        setMfaUserLogs(response.data.data);
        // Update pagination state
        if (response.data.pagination) {
          setMfaLogsPagination(response.data.pagination);
        }
      } else {
        setMfaUserLogsError(response.data?.message || 'Failed to fetch MFA user logs');
      }
    } catch (err) {
      console.error('Error fetching MFA user logs:', err);
      setMfaUserLogsError(err.message || 'An error occurred while fetching MFA user logs');
    } finally {
      setMfaUserLogsLoading(false);
    }
  };

  const handleToggleEnabled = async (user) => {
    setLoading(true);
    setError(null);
    try {
      const newStatus = !user.is_enabled;
      const response = await adminApi.patch(`/admin/mfa/users/${user.id}/manage/`, { is_enabled: newStatus });
      if (response.status === 200) { // Check for successful HTTP status
        fetchMfaUsers(); // Refresh the list to show updated status
      } else {
        // Use the message from the response if available, otherwise a generic error
        setError(response.data?.message || 'Failed to toggle status.');
      }
    } catch (err) {
      console.error('Error toggling MFA user status:', err);
      setError(err.response?.data?.error || err.message || 'An error occurred while toggling status.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading MFA users...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger">
        Error: {error}
      </div>
    );
  }

  return (
    <>
      {/* Page Title */}
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0 font-size-18">MFA Module</h4>
            <div className="page-title-right">
              <ol className="breadcrumb m-0">
                <li className="breadcrumb-item"><a href="#">Admin</a></li>
                <li className="breadcrumb-item active">MFA Module</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="d-flex justify-content-end align-items-center mb-4">
        <button className="btn btn-primary" onClick={handleCreateClick}>
          <i className="fas fa-plus-circle me-1"></i> Create MFA User
        </button>
      </div>

      {mfaUsers.length === 0 ? (
        <div className="alert text-secondary text-center">
          No MFA users found. Let start creating your first one. Click Create MFA User.
        </div>
      ) : (
        <AgGridTable
                  rowData={mfaUsers}
                  columnDefs={[
                    {
                      headerName: 'MFA User',
                      field: 'email',
                      width: 200,
                      valueGetter: (p) => `${p.data?.first_name || ''} ${p.data?.last_name || ''} ${p.data?.email || ''}`,
                      cellRenderer: (params) => {
                        if (!params.data) return null;
                        return (
                          <div>
                            <div>{params.data.first_name} {params.data.last_name}</div>
                            <small className="text-muted">{params.data.email}</small>
                          </div>
                        );
                      }
                    },
                    {
                      headerName: 'MFA Application',
                      field: 'application',
                      width: 150
                    },
                    {
                      headerName: 'Owned By',
                      field: 'owner_name',
                      width: 180,
                      valueGetter: (p) => `${p.data?.owner_name || ''} ${p.data?.owner_email || ''}`,
                      cellRenderer: (params) => {
                        if (!params.data) return null;
                        return (
                          <div>
                            <div>{params.data.owner_name}</div>
                            <small className="text-muted">{params.data.owner_email}</small>
                          </div>
                        );
                      }
                    },
                    {
                      headerName: 'Enabled',
                      field: 'is_enabled',
                      width: 100,
                      valueGetter: (p) => p.data?.is_enabled ? 'Yes' : 'No',
                      cellRenderer: (params) => {
                        if (!params.data) return null;
                        return (
                          <span className={`badge rounded-pill ${params.data.is_enabled ? 'bg-success' : 'bg-danger'}`}>
                            {params.data.is_enabled ? 'Yes' : 'No'}
                          </span>
                        );
                      }
                    },
                    {
                      headerName: 'Created At',
                      field: 'created_at',
                      width: 160,
                      valueGetter: (p) => p.data?.created_at ? new Date(p.data.created_at).toLocaleString() : 'N/A'
                    },
                    {
                      headerName: 'Actions',
                      field: 'actions',
                      minWidth: 380,
                      sortable: false,
                      filter: false,
                      cellRenderer: (params) => {
                        if (!params.data) return null;
                        const user = params.data;
                        return (
                          <div className="btn-group btn-group-sm" role="group">
                            <button className="btn btn-outline-info" onClick={() => handleGenerateTotpClick(user)}>TOTP</button>
                            <button className="btn btn-outline-warning" onClick={() => handleGenerateApiTokenClick(user)}>API Tokens</button>
                            <button className="btn btn-outline-secondary" onClick={() => handleAuditTrailClick(user)}>Audit</button>
                            <button className="btn btn-outline-primary" onClick={() => handleEditClick(user)}>Edit</button>
                            {user.is_enabled ? (
                              <button className="btn btn-outline-danger" onClick={() => handleToggleEnabled(user)}>Disable</button>
                            ) : (
                              <button className="btn btn-outline-success" onClick={() => handleToggleEnabled(user)}>Enable</button>
                            )}
                          </div>
                        );
                      }
                    }
                  ]}
                  defaultColDef={{
                    flex: 1,
                    minWidth: 100,
                    resizable: true,
                    sortable: true,
                    filter: true,
                  }}
                  gridOptions={{
                    autoSizeStrategy: { type: 'fitGridWidth' }
                  }}
                  getRowId={(params) => `${params.data.id}`}
                />
      )}

      <SidebarPanel
        isOpen={showForm || showOtpDisplay || showApiTokenSidebar || showAuditTrailSidebar}
        onClose={handleCloseForm}
        title={
          showForm ? (editingUser ? 'Edit MFA User' : 'Create MFA User') :
          showOtpDisplay ? 'Generate TOTP' :
          showApiTokenSidebar ? 'API Token Management' :
          'Audit Trail'
        }
      >
        {showForm && <MfaUserForm onClose={handleCloseForm} onSave={handleSave} initialData={editingUser} />}
        {showApiTokenSidebar && (
          <ApiTokenManager user={selectedUserForApiToken} onClose={handleCloseForm} />
        )}
        {showOtpDisplay && (
          <MfaUserForm onClose={handleCloseForm} onSave={handleSave} initialData={otpUser} showOnlyOtp={true} autoGenerateOtp={true} />
        )}
        {showAuditTrailSidebar && (
          <div className="audit-trail-container">
            <h5>Audit Trail for {selectedUserForAuditTrail?.email}</h5>
            {mfaUserLogsLoading && <div className="text-center">Loading logs...</div>}
            {mfaUserLogsError && <div className="alert alert-danger">{mfaUserLogsError}</div>}
            {!mfaUserLogsLoading && !mfaUserLogsError && (
              <div className="logs-container">
                {mfaUserLogs.length === 0 ? (
                  <div className="text-muted">No logs found for this user.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-striped table-hover mt-3">
                      <thead>
                        <tr>
                          <th>Timestamp</th>
                          <th>Access Type</th>
                          <th>Result</th>
                          <th>Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mfaUserLogs.map((log) => (
                          <tr key={log.id}>
                            <td>{new Date(log.timestamp).toLocaleString()}</td>
                            <td>{log.access_type || 'N/A'}</td>
                            <td>
                              <span className={`badge ${log.result === 'success' ? 'bg-success' : 'bg-danger'}`}>
                                {log.result}
                              </span>
                            </td>
                            <td>
                              <div className="small">
                                {log.ip_address && <div><strong>IP:</strong> {log.ip_address}</div>}
                                {log.user_agent && <div><strong>User Agent:</strong> {log.user_agent}</div>}
                                {log.mfa_details?.mfa_type && <div><strong>MFA Type:</strong> {log.mfa_details.mfa_type}</div>}
                                {log.mfa_details?.api_token_id && <div><strong>API Token ID:</strong> {log.mfa_details.api_token_id}</div>}
                                {log.mfa_details?.api_token_type && <div><strong>API Token Type:</strong> {log.mfa_details.api_token_type}</div>}
                                {log.mfa_details?.description && <div><strong>Description:</strong> {log.mfa_details.description}</div>}
                                {log.error_message && <div className="text-danger"><strong>Error:</strong> {log.error_message}</div>}
                                {log.error_code && <div className="text-danger"><strong>Error Code:</strong> {log.error_code}</div>}
                                {!log.ip_address && !log.user_agent && !log.mfa_details && !log.error_message && !log.error_code && (
                                  <span className="text-muted">No specific details available.</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                
                {/* Pagination Controls for MFA User Logs */}
                {mfaLogsPagination && (
                  <div className="mt-3">
                    <PaginationControls
                      pagination={mfaLogsPagination}
                      onPageChange={handleMfaLogsPageChange}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </SidebarPanel>
    </>
  );
};

export default MfaModulePage;